import { useContext, useMemo } from 'react';
import styled from 'styled-components';

import V3ContentScreenContext from './V3ContentScreenContext';
import { contentTypeCheckers } from '../ContentIndex/useContent';
import Tooltip from '../../../components/Tooltip';
import Typography from '../../../kingpin/atoms/Typography';
import CurrentUserContext from '../../../contexts/CurrentUserContext';
import ImpersonatorContext from '../../../contexts/ImpersonatorContext';
import Icon from '../../../kingpin/atoms/Icon';

const IndicatorDiv = styled.div`
  height: 23px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 8px;
  border: 1px solid var(--color-badge-warning-border);
  padding: 4px 6px;
  width: min-content;
  background-color: var(--color-badge-warning-bg);
`;

export const V3PrivateIndicator = ({ tooltip }: { tooltip?: string }) => {
  return (
    <Tooltip content={tooltip}>
      <IndicatorDiv>
        <Icon
          icon={'lock'}
          height={11}
          width={11}
          color={'var(--color-badge-yellow-text)'}
        />
        <Typography.Body type={'Badge'} color="var(--color-avatar-3-text)">
          Private
        </Typography.Body>
      </IndicatorDiv>
    </Tooltip>
  );
};

const V3PrivateIndicatorContainer = () => {
  const currentUser = useContext(CurrentUserContext);
  const { impersonatorId } = useContext(ImpersonatorContext);
  const { content } = useContext(V3ContentScreenContext);

  const currentUserId = useMemo(() => {
    if (impersonatorId) {
      return impersonatorId;
    }

    return currentUser.id;
  }, [currentUser.id, impersonatorId]);

  const contentLabel = useMemo(() => {
    if (!content) {
      return '';
    }

    if (contentTypeCheckers.isDashboard(content)) {
      return 'Dashboard';
    } else if (contentTypeCheckers.isReport(content)) {
      return 'Report';
    } else if (contentTypeCheckers.isGoal(content)) {
      return 'Goal';
    } else if (contentTypeCheckers.isScorecard(content)) {
      return 'KPI List';
    } else {
      return '';
    }
  }, [content]);

  const isV3Published = useMemo((): boolean | undefined => {
    if (!content) {
      return undefined;
    }

    if (contentTypeCheckers.isDashboard(content)) {
      return content.dashboard.isV3Published;
    } else if (contentTypeCheckers.isReport(content)) {
      return content.report.isV3Published;
    } else if (contentTypeCheckers.isGoal(content)) {
      return content.goal.isV3Published;
    } else if (contentTypeCheckers.isScorecard(content)) {
      return content.scorecard.isV3Published;
    }

    return undefined;
  }, [content]);

  const isCurrentUserCreator = useMemo(() => {
    if (!content) {
      return false;
    }

    if (contentTypeCheckers.isDashboard(content)) {
      return content.dashboard.createdBy === currentUserId;
    } else if (contentTypeCheckers.isReport(content)) {
      return content.report.createdBy === currentUserId;
    } else if (contentTypeCheckers.isGoal(content)) {
      return content.goal.createdBy === currentUserId;
    } else if (contentTypeCheckers.isScorecard(content)) {
      return content.scorecard.createdBy === currentUserId;
    }
  }, [content, currentUserId]);

  if (isV3Published === undefined || !!isV3Published) {
    return null;
  }

  return (
    <V3PrivateIndicator
      tooltip={`The ${contentLabel} is unpublished and is only visible to ${isCurrentUserCreator ? 'you' : 'the creator'}`}
    />
  );
};

export default V3PrivateIndicatorContainer;
